import { useQuery } from '@tanstack/react-query'
import { Crown, LogOut, UserPlus, UserRoundCog } from 'lucide-react'
import { useEffect, useState } from 'react'

import { loanOfficersQueryOptions } from '@/api/reference-api'
import type {
  CustomerSummary,
  DualControlResult,
  GroupDetail,
  GroupMember,
  GroupPosition,
  MembershipStatus,
} from '@/api/types'
import { ErrorSurface } from '@/components/error-surface'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { MemberPicker } from '@/features/customers/member-picker'
import { formatShortDate } from '@/lib/datetime'

import {
  GROUP_POSITION_OPTIONS,
  MEMBER_EXIT_CONSEQUENCES,
  MEMBER_EXIT_STATUSES,
  membershipStatusLabel,
} from './constants'
import { DualControlNotice, DualControlOutcome } from './dual-control'
import { GroupActionDialog } from './group-action-dialog'
import {
  useAddGroupMember,
  useAssignGroupOfficer,
  useChangeGroupLeader,
  useExitGroupMember,
} from './mutations'
import { GROUP_PERMISSIONS, useGroupPermissions } from './permissions'

/** A reason short enough to be meaningless is not a reason anyone can act on later. */
const MINIMUM_REASON_LENGTH = 5

const today = () => new Date().toISOString().slice(0, 10)

/**
 * Admitting a member.
 *
 * Only approved-KYC customers are offered, which mirrors the rule the API enforces rather than
 * replacing it: every member is KYC'd individually, and the group is never the customer. The
 * search is deliberately narrow — an exact-ish lookup by name or customer number — because this is
 * a screen for adding a person somebody is standing in front of, not for browsing the register.
 */
export function AddMemberDialog({
  group,
  open,
  onOpenChange,
}: {
  group: GroupDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [selected, setSelected] = useState<CustomerSummary | null>(null)
  const [position, setPosition] = useState<GroupPosition>('Member')
  const [joinedDate, setJoinedDate] = useState(today)

  const add = useAddGroupMember(group.id)

  useEffect(() => {
    if (!open) {
      setSelected(null)
      setPosition('Member')
      setJoinedDate(today())
      add.reset()
    }
    // `add` is a stable mutation object from react-query; resetting on close is the whole intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Somebody already in this group is not a candidate. The server refuses it as
  // `groups.member_already_active`; keeping them out of the list means nobody has to be refused.
  const existingCustomerIds = new Set(
    group.members.filter((member) => member.status === 'Active').map((member) => member.customerId),
  )

  return (
    <GroupActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add a member"
      description={`${group.name} — ${String(group.activeMemberCount)} active members today.`}
      confirmLabel="Add member"
      isPending={add.isPending}
      disabled={selected === null || joinedDate === ''}
      error={add.isError ? add.error : null}
      onConfirm={() => {
        if (!selected) {
          return
        }

        add.mutate(
          { customerId: selected.id, position, joinedDate },
          {
            onSuccess: () => {
              onOpenChange(false)
            },
          },
        )
      }}
    >
      <MemberPicker
        id="add-member"
        label="Find the customer"
        selected={selected}
        onSelect={setSelected}
        branchId={group.branchId}
        kycStatus="Approved"
        excludeCustomerIds={existingCustomerIds}
        helpText="Only customers whose KYC file is approved can join a group, so only those are listed. Someone already in another group will be refused when you confirm."
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="add-member-position">Position</Label>
          <Select value={position} onValueChange={(value) => setPosition(value as GroupPosition)}>
            <SelectTrigger id="add-member-position">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GROUP_POSITION_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="add-member-joined">Joined</Label>
          <Input
            id="add-member-joined"
            type="date"
            value={joinedDate}
            max={today()}
            onChange={(event) => setJoinedDate(event.target.value)}
          />
        </div>
      </div>
    </GroupActionDialog>
  )
}

/**
 * Ending a membership.
 *
 * How it ended is part of the record and is not offered as a formality: a member who walked away,
 * one the group removed, and one who died are three different facts, and the next officer to read
 * this file will be reading exactly this line.
 *
 * The leader cannot be the one leaving. The API refuses it — a leader who has left is a group with
 * no functioning signatory and a name on screen that means nothing — and the refusal names
 * appointing a replacement as the next step, so this dialog says the same thing first rather than
 * letting someone find out after typing a reason.
 */
export function ExitMemberDialog({
  group,
  member,
  open,
  onOpenChange,
}: {
  group: GroupDetail
  member: GroupMember | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [status, setStatus] = useState<Exclude<MembershipStatus, 'Active'>>('Exited')
  const [leftDate, setLeftDate] = useState(today)
  const [reason, setReason] = useState('')

  const exit = useExitGroupMember(group.id)

  useEffect(() => {
    if (!open) {
      setStatus('Exited')
      setLeftDate(today())
      setReason('')
      exit.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!member) {
    return null
  }

  const isLeader = member.position === 'Leader'
  const trimmed = reason.trim()

  return (
    <GroupActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="End this membership"
      description={`${member.fullName} — a member of ${group.name} since ${formatShortDate(member.joinedDate)}.`}
      confirmLabel="End membership"
      tone="danger"
      isPending={exit.isPending}
      disabled={isLeader || trimmed.length < MINIMUM_REASON_LENGTH || leftDate === ''}
      error={exit.isError ? exit.error : null}
      onConfirm={() => {
        exit.mutate(
          { customerId: member.customerId, status, leftDate, reason: trimmed },
          {
            onSuccess: () => {
              onOpenChange(false)
            },
          },
        )
      }}
    >
      {isLeader ? (
        <div className="rounded-card border border-warning-border bg-warning-subtle p-3 text-sm text-body">
          <p className="font-semibold text-heading">Appoint another leader first</p>
          <p className="mt-1">
            {member.fullName} leads this group. A leader cannot be recorded as leaving until another
            member has been made leader — otherwise the group is left with no signatory and a name on
            screen that means nothing. Change the leader, then come back here.
          </p>
        </div>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="exit-member-status">How the membership ended</Label>
        <Select
          value={status}
          onValueChange={(value) => setStatus(value as Exclude<MembershipStatus, 'Active'>)}
          disabled={isLeader}
        >
          <SelectTrigger id="exit-member-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MEMBER_EXIT_STATUSES.map((value) => (
              <SelectItem key={value} value={value}>
                {membershipStatusLabel(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-body">{MEMBER_EXIT_CONSEQUENCES[status]}</p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="exit-member-date">Date</Label>
        <Input
          id="exit-member-date"
          type="date"
          value={leftDate}
          max={today()}
          disabled={isLeader}
          onChange={(event) => setLeftDate(event.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="exit-member-reason">Reason</Label>
        <Input
          id="exit-member-reason"
          value={reason}
          autoComplete="off"
          disabled={isLeader}
          onChange={(event) => setReason(event.target.value)}
          aria-describedby="exit-member-reason-help"
        />
        <p id="exit-member-reason-help" className="text-xs text-muted">
          Kept on the membership and in the audit trail. It is what the next officer reads about
          this person.
        </p>
      </div>
    </GroupActionDialog>
  )
}

/**
 * Appointing a leader.
 *
 * Dual-controlled, and offered only over the group's own active members — the API enforces that
 * the leader is always an active member on every path in, not in a handler, because the rule is
 * about what a group *is* rather than about what this screen happens to send.
 */
export function ChangeLeaderDialog({
  group,
  open,
  onOpenChange,
}: {
  group: GroupDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const permissions = useGroupPermissions()
  const [customerId, setCustomerId] = useState('')
  const [reason, setReason] = useState('')
  const [result, setResult] = useState<DualControlResult | null>(null)

  const change = useChangeGroupLeader(group.id)

  useEffect(() => {
    if (!open) {
      setCustomerId('')
      setReason('')
      setResult(null)
      change.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const eligible = group.members.filter(
    (member) => member.status === 'Active' && member.customerId !== group.leaderCustomerId,
  )

  const chosen = eligible.find((member) => member.customerId === customerId)
  const trimmed = reason.trim()

  return (
    <GroupActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Change the group leader"
      description={
        group.leaderName
          ? `${group.name} is led by ${group.leaderName}.`
          : `${group.name} has no leader yet.`
      }
      notice={
        <DualControlNotice
          action="Changing the leader"
          approvePermission={GROUP_PERMISSIONS.leaderChangeApprove}
          canCountersign={permissions.canCountersignLeaderChange}
        />
      }
      outcome={
        result ? (
          <DualControlOutcome
            result={result}
            awaitingDescription={`Appointing ${chosen?.fullName ?? 'the new leader'} is waiting for a countersignature.`}
            appliedDescription={`${chosen?.fullName ?? 'The new leader'} now leads ${group.name}.`}
          />
        ) : null
      }
      confirmLabel="Change leader"
      isPending={change.isPending}
      disabled={customerId === '' || trimmed.length < MINIMUM_REASON_LENGTH}
      error={change.isError ? change.error : null}
      onConfirm={() => {
        change.mutate({ customerId, reason: trimmed }, { onSuccess: setResult })
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="change-leader-member">New leader</Label>
        {eligible.length === 0 ? (
          <p className="rounded-card border border-border-subtle bg-surface-muted p-3 text-xs text-body">
            There is no other active member to appoint. Add a member first — a leader is always one
            of the group&apos;s own.
          </p>
        ) : (
          <Select value={customerId} onValueChange={setCustomerId}>
            <SelectTrigger id="change-leader-member">
              <SelectValue placeholder="Select an active member…" />
            </SelectTrigger>
            <SelectContent>
              {eligible.map((member) => (
                <SelectItem key={member.customerId} value={member.customerId}>
                  {member.fullName} · {member.customerNumber}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="change-leader-reason">Reason</Label>
        <Input
          id="change-leader-reason"
          value={reason}
          autoComplete="off"
          onChange={(event) => setReason(event.target.value)}
          aria-describedby="change-leader-reason-help"
        />
        <p id="change-leader-reason-help" className="text-xs text-muted">
          Read by whoever countersigns this, and kept in the audit trail afterwards.
        </p>
      </div>
    </GroupActionDialog>
  )
}

/**
 * Assigning the responsible officer.
 *
 * Dual-controlled. The directory is filtered to loan officers at the group's own branch, because
 * those are the only assignments the server will accept — `groups.officer_not_a_loan_officer` and
 * `groups.officer_wrong_branch` are the two refusals this list is shaped to avoid.
 */
export function AssignOfficerDialog({
  group,
  open,
  onOpenChange,
}: {
  group: GroupDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const permissions = useGroupPermissions()
  const [officerId, setOfficerId] = useState('')
  const [reason, setReason] = useState('')
  const [result, setResult] = useState<DualControlResult | null>(null)

  const officers = useQuery(loanOfficersQueryOptions(open ? group.branchId : null))
  const assign = useAssignGroupOfficer(group.id)

  useEffect(() => {
    if (!open) {
      setOfficerId('')
      setReason('')
      setResult(null)
      assign.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const available = (officers.data ?? []).filter((staff) => staff.userId !== group.loanOfficerId)
  const chosen = available.find((staff) => staff.userId === officerId)
  const trimmed = reason.trim()

  return (
    <GroupActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Assign the loan officer"
      description={
        group.loanOfficerName
          ? `${group.name} is currently with ${group.loanOfficerName}.`
          : `${group.name} has no officer assigned.`
      }
      notice={
        <DualControlNotice
          action="Assigning the officer"
          approvePermission={GROUP_PERMISSIONS.officerAssignApprove}
          canCountersign={permissions.canCountersignOfficerAssign}
        />
      }
      outcome={
        result ? (
          <DualControlOutcome
            result={result}
            awaitingDescription={`Handing ${group.name} to ${chosen?.fullName ?? 'the new officer'} is waiting for a countersignature.`}
            appliedDescription={`${group.name} is now with ${chosen?.fullName ?? 'the new officer'}.`}
          />
        ) : null
      }
      confirmLabel="Assign officer"
      isPending={assign.isPending}
      disabled={officerId === '' || trimmed.length < MINIMUM_REASON_LENGTH}
      error={assign.isError ? assign.error : null}
      onConfirm={() => {
        assign.mutate({ loanOfficerId: officerId, reason: trimmed }, { onSuccess: setResult })
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="assign-officer-user">Loan officer</Label>
        {officers.isError ? (
          <ErrorSurface
            error={officers.error}
            onRetry={() => void officers.refetch()}
            title="The officer directory could not be read"
          />
        ) : officers.isPending ? (
          <p className="flex items-center gap-2 p-3 text-xs text-muted">
            <Spinner className="size-4" />
            Reading the directory…
          </p>
        ) : available.length === 0 ? (
          <p className="rounded-card border border-border-subtle bg-surface-muted p-3 text-xs text-body">
            No other member of staff at this branch is flagged as a loan officer. An administrator
            sets that flag on the user record.
          </p>
        ) : (
          <Select value={officerId} onValueChange={setOfficerId}>
            <SelectTrigger id="assign-officer-user">
              <SelectValue placeholder="Select an officer…" />
            </SelectTrigger>
            <SelectContent>
              {available.map((staff) => (
                <SelectItem key={staff.userId} value={staff.userId}>
                  {staff.fullName}
                  {staff.employeeCode ? ` · ${staff.employeeCode}` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="assign-officer-reason">Reason</Label>
        <Input
          id="assign-officer-reason"
          value={reason}
          autoComplete="off"
          onChange={(event) => setReason(event.target.value)}
          aria-describedby="assign-officer-reason-help"
        />
        <p id="assign-officer-reason-help" className="text-xs text-muted">
          A handover is the moment a group stops being one person&apos;s responsibility and becomes
          another&apos;s. Say why.
        </p>
      </div>
    </GroupActionDialog>
  )
}

/** The buttons that open the four membership dialogs, with the permission each one needs. */
export function MembershipActions({
  group,
  onAddMember,
  onChangeLeader,
  onAssignOfficer,
}: {
  group: GroupDetail
  onAddMember: () => void
  onChangeLeader: () => void
  onAssignOfficer: () => void
}) {
  const permissions = useGroupPermissions()
  const dissolved = group.status === 'Dissolved'

  return (
    <div className="flex flex-wrap items-center gap-2">
      {permissions.canAddMember ? (
        <Button variant="primary" size="sm" onClick={onAddMember} disabled={dissolved}>
          <UserPlus />
          Add member
        </Button>
      ) : null}

      {permissions.canChangeLeader ? (
        <Button variant="secondary" size="sm" onClick={onChangeLeader} disabled={dissolved}>
          <Crown />
          Change leader
        </Button>
      ) : null}

      {permissions.canAssignOfficer ? (
        <Button variant="secondary" size="sm" onClick={onAssignOfficer} disabled={dissolved}>
          <UserRoundCog />
          Assign officer
        </Button>
      ) : null}
    </div>
  )
}

/** The per-row control that ends a membership. Hidden without the permission, absent for leavers. */
export function ExitMemberButton({
  member,
  onExit,
  disabled,
}: {
  member: GroupMember
  onExit: (member: GroupMember) => void
  disabled: boolean
}) {
  const permissions = useGroupPermissions()

  if (!permissions.canExitMember || member.status !== 'Active') {
    return null
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => onExit(member)}
      disabled={disabled}
      aria-label={`End ${member.fullName}'s membership`}
    >
      <LogOut />
      Exit
    </Button>
  )
}
